<script lang="ts">
  import { landingPath, type Resolution } from "$lib/auth/guards";
  import { preferredPortalOrganization } from "$lib/domain/org";
  import { router, link } from "../../router.svelte";
  import { session } from "$lib/stores/session.svelte";
  import { prefs } from "$lib/stores/prefs.svelte";
  import { loadPage } from "$lib/release/loadPage";
  import AppLogo from "$lib/components/AppLogo.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Badge from "$lib/components/Badge.svelte";
  import Drawer from "$lib/components/Drawer.svelte";
  import AccountMenu from "$lib/components/AccountMenu.svelte";
  import type { MenuItem } from "$lib/components/types";
  import AdminCapabilityDenied from "./AdminCapabilityDenied.svelte";

  interface Props {
    view: Extract<Resolution, { kind: "admin" | "admin-denied" }>;
  }
  const { view }: Props = $props();

  // The customer list, every customer detail page, and the quote composer all live
  // under the same nav item.
  const entitlementsActive = $derived(
    view.name === "admin.entitlements" || view.name === "admin.customer" || view.name === "admin.customer.quote.new"
  );
  const customerId = $derived(
    view.name === "admin.customer" || view.name === "admin.customer.quote.new"
      ? router.match.params.customerId ?? ""
      : ""
  );
  // Organization recovery is a distinct support capability; the nav item only exists
  // for operators who actually hold it (the route itself 404s without it).
  const organizationsActive = $derived(view.name === "admin.organizations");
  // Contract library (templates) and the signing queue are separate nav items, each
  // owning its own detail pages. Keyed by id so switching records remounts the page.
  const contractsActive = $derived(
    view.name === "admin.contracts" || view.name === "admin.contract" || view.name === "admin.contract.new"
  );
  const contractQueueActive = $derived(view.name === "admin.contract-queue" || view.name === "admin.contract-queue.item");
  const contractId = $derived(
    view.name === "admin.contract" || view.name === "admin.contract-queue.item"
      ? router.match.params.contractId ?? ""
      : ""
  );
  const revenueActive = $derived(view.name === "admin.revenue");
  const auditActive = $derived(view.name === "admin.audit");
  // Discord operations exist only where the server grants `discord.manage`; the nav
  // item follows the capability the same way Organizations does.
  const discordActive = $derived(view.name === "admin.discord");

  const organizationView = $derived(
    preferredPortalOrganization(session.organizations, prefs.lastOrgId)
  );
  const accountMenuItems = $derived<MenuItem[]>(
    organizationView
      ? [{
          id: "organization-view",
          label: "Switch to customer portal",
          description: organizationView.name,
          onSelect: () => router.navigate(landingPath(organizationView, organizationView.id))
        }]
      : []
  );

  $effect(() => {
    const path = view.name === "admin.organizations"
      ? "/admin/organizations"
      : view.name === "admin.revenue"
        ? "/admin/revenue"
        : view.name === "admin.audit"
          ? "/admin/audit"
          : view.name === "admin.discord"
            ? "/admin/discord"
            : contractsActive
              ? "/admin/contracts"
              : contractQueueActive ? "/admin/contract-queue" : "/admin/entitlements";
    if (prefs.lastAdminPath !== path) prefs.setLastAdminPath(path);
  });

  let navOpen = $state(false);
</script>

{#snippet loadingPage()}
  <div class="page-loading" role="status" aria-live="polite"><span class="sr-only">Loading page…</span></div>
{/snippet}

{#snippet pageLoadError()}
  <Banner tone="error" title="We couldn't load this page" role="alert">
    Refresh the page and try again.
  </Banner>
{/snippet}

{#snippet adminNav()}
  <nav aria-label="Admin navigation">
    <ul class="nav">
      <li>
        <a
          href="/admin/entitlements"
          use:link
          class="nav-link"
          class:active={entitlementsActive}
          aria-current={entitlementsActive ? "page" : undefined}
        >
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 6.5h12M4 10h12M4 13.5h8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
          Customers
        </a>
      </li>
      {#if session.canSupportOrganizations}
        <li>
          <a
            href="/admin/organizations"
            use:link
            class="nav-link"
            class:active={organizationsActive}
            aria-current={organizationsActive ? "page" : undefined}
          >
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 16.5V6.2L9 3.5v13M9 8.3h6.2a1.3 1.3 0 0 1 1.3 1.3v6.9M6 8.4h.01M6 11.4h.01M12 11.4h.01M12 14h.01" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
            Organizations
          </a>
        </li>
      {/if}
      {#if session.canManageInvoices}
        <li>
          <a href="/admin/contracts" use:link class="nav-link" class:active={contractsActive} aria-current={contractsActive ? "page" : undefined}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5.5 3.5h7l3 3v10h-10zM12.5 3.5v3h3M7.5 10h5M7.5 13h5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
            Contracts
          </a>
        </li>
        <li>
          <a href="/admin/contract-queue" use:link class="nav-link" class:active={contractQueueActive} aria-current={contractQueueActive ? "page" : undefined}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 5.5h12M4 10h8M4 14.5h5M14 12.5l1.5 1.5 3-3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
            Pending Contracts
          </a>
        </li>
      {/if}
      <li>
        <a href="/admin/revenue" use:link class="nav-link" class:active={revenueActive} aria-current={revenueActive ? "page" : undefined}>
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 15.5V10m4.3 5.5V6.5m4.3 9V9m4.4 6.5V3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
          Revenue
        </a>
      </li>
      <li>
        <a href="/admin/audit" use:link class="nav-link" class:active={auditActive} aria-current={auditActive ? "page" : undefined}>
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 3.5h10v13H5zM7.5 7h5M7.5 10h5M7.5 13h3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
          Audit
        </a>
      </li>
      {#if session.canManageDiscord}
        <li>
          <a href="/admin/discord" use:link class="nav-link" class:active={discordActive} aria-current={discordActive ? "page" : undefined}>
            <span class="nav-glyph" aria-hidden="true">#</span>
            Discord
          </a>
        </li>
      {/if}
    </ul>
  </nav>
{/snippet}

<div class="shell">
  <aside class="sidebar">
    <a class="brand" href="/admin/entitlements" use:link>
      <AppLogo size={28} />
      <span>EpiDev Admin</span>
    </a>
    <div class="scope-label">Support operations</div>
    {@render adminNav()}
  </aside>

  <div class="main-col">
    <header class="topbar">
      <button class="hamburger" aria-label="Open navigation" aria-expanded={navOpen} onclick={() => (navOpen = true)}>
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
      </button>
      <div class="topbar-title">
        <AppLogo size={24} />
        <span class="wordmark">Administration</span>
      </div>
      <div class="topbar-right">
        <Badge tone="accent">Support admin</Badge>
        <AccountMenu items={accountMenuItems} />
      </div>
    </header>

    <main id="main-content" class="content">
      {#if view.kind === "admin-denied"}
        <AdminCapabilityDenied capability={view.capability} />
      {:else if view.name === "admin.entitlements"}
        {#await loadPage("admin.entitlements", () => import("./AdminEntitlements.svelte"))}
          {@render loadingPage()}
        {:then module}
          <module.default />
        {:catch}
          {@render pageLoadError()}
        {/await}
      {:else if view.name === "admin.revenue"}
        {#await loadPage("admin.revenue", () => import("./AdminRevenue.svelte"))}
          {@render loadingPage()}
        {:then module}
          <module.default />
        {:catch}
          {@render pageLoadError()}
        {/await}
      {:else if view.name === "admin.organizations"}
        {#await loadPage("admin.organizations", () => import("./AdminOrganizations.svelte"))}
          {@render loadingPage()}
        {:then module}
          <module.default />
        {:catch}
          {@render pageLoadError()}
        {/await}
      {:else if view.name === "admin.audit"}
        {#await loadPage("admin.audit", () => import("./AdminAudit.svelte"))}
          {@render loadingPage()}
        {:then module}
          <module.default />
        {:catch}
          {@render pageLoadError()}
        {/await}
      {:else if view.name === "admin.discord"}
        {#await loadPage("admin.discord", () => import("./AdminDiscord.svelte"))}
          {@render loadingPage()}
        {:then module}
          <module.default />
        {:catch}
          {@render pageLoadError()}
        {/await}
      {:else if view.name === "admin.contracts"}
        {#await loadPage("admin.contracts", () => import("./AdminContracts.svelte"))}
          {@render loadingPage()}
        {:then module}
          <module.default />
        {:catch}
          {@render pageLoadError()}
        {/await}
      {:else if view.name === "admin.contract.new"}
        {#await loadPage("admin.contract.new", () => import("./AdminContractDetail.svelte"))}
          {@render loadingPage()}
        {:then module}
          <module.default contractId={null} />
        {:catch}
          {@render pageLoadError()}
        {/await}
      {:else if view.name === "admin.contract"}
        {#key contractId}
          {#await loadPage("admin.contract", () => import("./AdminContractDetail.svelte"))}
            {@render loadingPage()}
          {:then module}
            <module.default {contractId} />
          {:catch}
            {@render pageLoadError()}
          {/await}
        {/key}
      {:else if view.name === "admin.contract-queue"}
        {#await loadPage("admin.contract-queue", () => import("./AdminContractQueue.svelte"))}
          {@render loadingPage()}
        {:then module}
          <module.default />
        {:catch}
          {@render pageLoadError()}
        {/await}
      {:else if view.name === "admin.contract-queue.item"}
        {#key contractId}
          {#await loadPage("admin.contract-queue.item", () => import("./AdminContractQueueDetail.svelte"))}
            {@render loadingPage()}
          {:then module}
            <module.default {contractId} />
          {:catch}
            {@render pageLoadError()}
          {/await}
        {/key}
      {:else if view.name === "admin.customer"}
        <!-- Keyed so switching customers remounts the page: every per-customer
             fetch, dialog, and tab selection resets instead of leaking across. -->
        {#key customerId}
          {#await loadPage("admin.customer", () => import("./AdminCustomerDetail.svelte"))}
            {@render loadingPage()}
          {:then module}
            <module.default {customerId} />
          {:catch}
            {@render pageLoadError()}
          {/await}
        {/key}
      {:else if view.name === "admin.customer.quote.new"}
        <!-- Keyed for the same reason: a half-composed quote must never carry over
             onto a different customer. -->
        {#key customerId}
          {#await loadPage("admin.customer.quote.new", () => import("./AdminQuoteComposer.svelte"))}
            {@render loadingPage()}
          {:then module}
            <module.default {customerId} />
          {:catch}
            {@render pageLoadError()}
          {/await}
        {/key}
      {/if}
    </main>
  </div>
</div>

{#if navOpen}
  <Drawer open={navOpen} title="Admin navigation" onclose={() => (navOpen = false)}>
    {@render adminNav()}
  </Drawer>
{/if}

<style>
  .shell {
    display: grid;
    grid-template-columns: var(--sidebar-w) 1fr;
    min-height: 100vh;
  }
  .sidebar {
    border-right: 1px solid var(--line-soft);
    padding: 18px 14px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: var(--bg-2-veil);
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--heading);
    font-weight: 650;
    padding: 4px 6px;
  }
  .scope-label {
    padding: 0 8px;
    color: var(--muted);
    font-size: 11px;
    font-weight: 650;
    letter-spacing: 0;
    text-transform: uppercase;
  }
  .nav {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .nav-link {
    min-height: 42px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    border-radius: var(--radius-btn);
    color: var(--muted);
    font-size: 14px;
    font-weight: 550;
  }
  .nav-link svg {
    width: 18px;
    height: 18px;
  }
  .nav-glyph {
    width: 18px;
    height: 18px;
    display: grid;
    place-items: center;
    font-family: var(--font-mono);
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
  }
  .nav-link.active {
    color: var(--heading);
    background: var(--accent-hover);
  }
  .main-col {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .topbar {
    height: var(--topbar-h);
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0 22px;
    border-bottom: 1px solid var(--line-soft);
    position: sticky;
    top: 0;
    /* Same site glass as the customer shell's topbar — see AppShell. */
    background: var(--glass);
    backdrop-filter: saturate(140%) blur(14px);
    -webkit-backdrop-filter: saturate(140%) blur(14px);
    z-index: 10;
  }
  .topbar-title,
  .topbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .topbar-right {
    margin-left: auto;
  }
  .wordmark {
    color: var(--heading);
    font-weight: 600;
  }
  .hamburger {
    display: none;
    width: 38px;
    height: 38px;
    place-items: center;
    border: 1px solid var(--line-soft);
    border-radius: 8px;
    background: transparent;
    color: var(--text);
    cursor: pointer;
  }
  .hamburger svg {
    width: 20px;
    height: 20px;
  }
  .content {
    width: 100%;
    /* The console's measure. Wider than the customer shell's --maxw-app because
       entitlement and quote tables are wide; centred so it does not drift left on a
       very large display. */
    max-width: var(--maxw-admin);
    margin: 0 auto;
    min-width: 0;
    flex: 1 1 auto;
    display: flex;
  }
  /* Desktop: the sidebar is pinned and the content column scrolls on its own, so
     the nav and topbar stay put while a long customer history scrolls. */
  @media (min-width: 1081px) {
    .shell {
      height: 100vh;
      overflow: hidden;
    }
    .sidebar {
      position: sticky;
      top: 0;
      height: 100vh;
    }
    .main-col {
      height: 100%;
      min-height: 0;
      overflow-y: auto;
    }
    .content {
      min-height: 0;
    }
  }
  @media (max-width: 1080px) {
    .shell {
      grid-template-columns: 1fr;
    }
    .sidebar {
      display: none;
    }
    .hamburger {
      display: grid;
    }
  }
  @media (max-width: 560px) {
    .wordmark,
    .topbar-right :global(.badge) {
      display: none;
    }
  }
</style>
